import { useState } from 'react';
import { Modal, Button, Table, Tag, Space, Typography, FloatButton } from 'antd';
import {
  BugOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { logger, LogLevel } from '../utils/logger';

const { Text } = Typography;

/**
 * Log Viewer Component
 *
 * Floating button that opens modal to view, export, and clear logs
 */
export default function LogViewer() {
  const [visible, setVisible] = useState(false);
  const [logs, setLogs] = useState(logger.getAllLogs());

  const handleOpen = () => {
    setLogs(logger.getAllLogs());
    setVisible(true);
  };

  const handleExport = () => {
    logger.exportLogs();
  };

  const handleExportJSON = () => {
    logger.exportLogsJSON();
  };

  const handleClear = () => {
    if (window.confirm("Barcha log'larni o'chirmoqchimisiz?")) {
      logger.clearLogs();
      setLogs([]);
    }
  };

  const stats = logger.getStats();

  const columns = [
    {
      title: 'Vaqt',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      defaultSortOrder: 'descend' as const,
      sorter: (a: { timestamp: string }, b: { timestamp: string }) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      render: (timestamp: string) => {
        const date = new Date(timestamp);
        const ms = date.getMilliseconds().toString().padStart(3, '0');
        return (
          <Text style={{ fontSize: 12 }}>
            {date.toLocaleTimeString('uz-UZ')}.{ms}
            <br />
            {date.toLocaleDateString('uz-UZ')}
          </Text>
        );
      },
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level: LogLevel) => {
        const colors = {
          DEBUG: 'default',
          INFO: 'blue',
          WARN: 'orange',
          ERROR: 'red',
        };
        return <Tag color={colors[level]}>{level}</Tag>;
      },
      filters: [
        { text: 'DEBUG', value: LogLevel.DEBUG },
        { text: 'INFO', value: LogLevel.INFO },
        { text: 'WARN', value: LogLevel.WARN },
        { text: 'ERROR', value: LogLevel.ERROR },
      ],
      onFilter: (value: any, record: any) => record.level === value,
    },
    {
      title: 'Xabar',
      dataIndex: 'message',
      key: 'message',
      render: (message: string, record: any) => (
        <div>
          <Text>{message}</Text>
          {record.data && (
            <pre
              style={{
                marginTop: 8,
                padding: 8,
                background: '#1a1a1a',
                color: '#00ff00',
                borderRadius: 4,
                fontSize: 11,
                overflow: 'auto',
                maxHeight: 200,
                border: '1px solid #00ff00',
              }}
            >
              {JSON.stringify(record.data, null, 2)}
            </pre>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Floating Action Button */}
      <FloatButton
        icon={<BugOutlined />}
        type="primary"
        tooltip="Log'larni ko'rish"
        onClick={handleOpen}
        style={{ right: 24, bottom: 24 }}
      />

      {/* Log Viewer Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>
              Loglar ({stats.total} ta)
            </span>
            <Tag color="default">{stats.byLevel[LogLevel.DEBUG]} DEBUG</Tag>
            <Tag color="blue">{stats.byLevel[LogLevel.INFO]} INFO</Tag>
            <Tag color="orange">{stats.byLevel[LogLevel.WARN]} WARN</Tag>
            <Tag color="red">{stats.byLevel[LogLevel.ERROR]} ERROR</Tag>
          </Space>
        }
        open={visible}
        onCancel={() => setVisible(false)}
        width="90%"
        style={{ top: 20 }}
        footer={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              TXT Export
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportJSON}>
              JSON Export
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleClear}>
              Tozalash
            </Button>
            <Button type="primary" onClick={() => setVisible(false)}>
              Yopish
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={logs}
          columns={columns}
          rowKey={(record) => record.timestamp}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total} ta log`,
          }}
          scroll={{ y: 500 }}
          size="small"
        />
      </Modal>
    </>
  );
}
